import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import styles from './home.module.css';
import { getComponentTranslator } from '../../util/i18n';
import AccessDenied from '../AccessDenied/AccessDenied';
import withEditRights from '../hoc/with-edit-rights';
import apiClient from '../../util/api-client';

const translate = getComponentTranslator('home');

function HomePage({ canEdit }) {
  const location = useLocation();
  const [competitions, setCompetitions] = useState(() => {
    const savedCompetitions = localStorage.getItem('competitions');
    return savedCompetitions ? JSON.parse(savedCompetitions) : [];
  });

  const [winners, setWinners] = useState(() => {
    const savedWinners = localStorage.getItem('winners');
    return savedWinners ? JSON.parse(savedWinners) : [];
  });

  const [classes, setClasses] = useState(() => {
    const savedClasses = localStorage.getItem('classes');
    return savedClasses ? JSON.parse(savedClasses) : [];
  });

  const [newCompetitionName, setNewCompetitionName] = useState('');
  const [newCompetitionPoints, setNewCompetitionPoints] = useState('');
  const [newCompetitionType, setNewCompetitionType] = useState('time');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const slideIntervalRef = useRef(null);

  // Sync with MongoDB backend on mount
  useEffect(() => {
    apiClient.getClasses().then((data) => {
      if (Array.isArray(data)) {
        const sortedClasses = [...data].sort((a, b) => (b.score || 0) - (a.score || 0));
        setClasses(sortedClasses);
      }
    });

    apiClient.getCompetitions().then((data) => {
      if (Array.isArray(data)) {
        setCompetitions(data);
      }
    });

    apiClient.getWinners().then((data) => {
      if (data && data.winners && data.winners.length) {
        setWinners(data.winners);
      }
    });
  }, []);

  useEffect(() => {
    const handleStorageUpdate = () => {
      const updatedClasses = localStorage.getItem('classes');
      if (updatedClasses) {
        const parsedClasses = JSON.parse(updatedClasses);
        // Sortera klasserna efter poäng endast för visning i leaderboard
        const sortedClasses = [...parsedClasses].sort((a, b) => (b.score || 0) - (a.score || 0));
        setClasses(sortedClasses);
      } else {
        setClasses([]);
      }
    };

    window.addEventListener('storage', handleStorageUpdate);
    // Kör sorteringen när komponenten mountas
    handleStorageUpdate();

    return () => {
      window.removeEventListener('storage', handleStorageUpdate);
    };
  }, []);

  // Auto-slide carousel för klasser med oändlig loop
  useEffect(() => {
    // Rensa gammal interval
    if (slideIntervalRef.current) {
      clearInterval(slideIntervalRef.current);
      slideIntervalRef.current = null;
    }

    if (classes.length > 0) {
      // Starta från början av andra kopian
      setCurrentSlide(classes.length);

      slideIntervalRef.current = setInterval(() => {
        setCurrentSlide((prev) => {
          // Alltid gå framåt, loopa tillbaka vid behov
          const next = (prev + 1) % (classes.length * 2);
          // Om vi når slutet, hoppa tillbaka till början av andra kopian
          if (next === 0 || next >= classes.length * 2) {
            return classes.length;
          }
          return next;
        });
      }, 3000);

      return () => {
        if (slideIntervalRef.current) {
          clearInterval(slideIntervalRef.current);
          slideIntervalRef.current = null;
        }
      };
    }
  }, [classes.length]);

  const addCompetition = () => {
    if (newCompetitionName.trim() && newCompetitionPoints.trim()) {
      const newCompetition = {
        name: newCompetitionName,
        type: newCompetitionType,
        points: newCompetitionPoints,
      };
      const updatedCompetitions = [...competitions, newCompetition];
      setCompetitions(updatedCompetitions);
      apiClient.saveCompetitions(updatedCompetitions);
      setNewCompetitionName('');
      setNewCompetitionPoints('');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      addCompetition();
    }
  };

  const removeCompetition = (index) => {
    const updatedCompetitions = competitions.filter((_, i) => i !== index);
    setCompetitions(updatedCompetitions);
    apiClient.saveCompetitions(updatedCompetitions);
  };

  const removeWinner = (index) => {
    const updatedWinners = winners.filter((_, i) => i !== index);
    setWinners(updatedWinners);
    apiClient.saveWinners(updatedWinners);
  };

  const updateWinner = (index) => {
    const updatedName = prompt('Ange nytt namn:', winners[index].name);
    const updatedScore = prompt('Ange ny poäng:', winners[index].score);

    if (updatedName && updatedScore !== null) {
      const updatedWinners = [...winners];
      updatedWinners[index] = { name: updatedName, score: parseInt(updatedScore) };
      updatedWinners.sort((a, b) => b.score - a.score);
      setWinners(updatedWinners);
      apiClient.saveWinners(updatedWinners);
    }
  };

  const removeClass = (id) => {
    const updatedClasses = classes.filter((cls) => {
      const cId = cls.id || cls._id;
      return cId !== id && cls.name !== id && cls._id !== id;
    });
    setClasses(updatedClasses);
    apiClient.saveClasses(updatedClasses);
    apiClient.deleteClass(id);
  };

  const displayedWinners = showMore ? winners : winners.slice(0, 3);

  // Check if user was redirected due to access denied
  if (location.state && location.state.accessDenied) {
    return <AccessDenied message={location.state.message} />;
  }

  return (
    <div className={styles.homeContainer}>
      <div className={styles.heroSection}>
        <img src="https://previews.123rf.com/images/kentoh/kentoh1704/kentoh170400434/76892430-sports-illustration-abstract-background-with-silhouette-art.jpg" alt="Tävling" className={styles.largeImage} />
        <div className={styles.heroOverlay}>
          <div className={styles.heroContent}>
            <h1 className={styles.heroTitle}>Välkommen till Tävlingar</h1>
            <p className={styles.heroSubtitle}>Spåra dina framsteg och tävla mot andra</p>
          </div>
        </div>
        <div className={styles.floatingShapes}>
          <div className={styles.shape1}></div>
          <div className={styles.shape2}></div>
          <div className={styles.shape3}></div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className={styles.statsSection}>
        <div className={styles.statCard}>
          <div className={styles.statIcon}>🏆</div>
          <div className={styles.statContent}>
            <div className={styles.statNumber}>{competitions.length}</div>
            <div className={styles.statLabel}>Aktiva Tävlingar</div>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon}>👥</div>
          <div className={styles.statContent}>
            <div className={styles.statNumber}>{classes.length}</div>
            <div className={styles.statLabel}>Deltagande Klasser</div>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon}>⭐</div>
          <div className={styles.statContent}>
            <div className={styles.statNumber}>
              {classes.reduce((sum, cls) => sum + (cls.score || 0), 0)}
            </div>
            <div className={styles.statLabel}>Totala Poäng</div>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon}>🎯</div>
          <div className={styles.statContent}>
            <div className={styles.statNumber}>
              {classes.length > 0 ? Math.max(...classes.map(c => c.score || 0)) : 0}
            </div>
            <div className={styles.statLabel}>Högsta Poäng</div>
          </div>
        </div>
      </div>

      <div className={styles.mainContent}>
        {/* Left Side (Home content) */}
        <div className={styles.homeContent}>
          <h2>{translate('Competitions')}</h2>
          <div className={styles.scrollableBody}>
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>{translate('Competition')}</th>
                  <th>{translate('Points')}</th>
                  {canEdit && <th>{translate('Action')}</th>}
                </tr>
              </thead>
              <tbody>
                {competitions.length === 0 ? (
                  <tr>
                    <td colSpan={canEdit ? "4" : "3"} style={{ textAlign: 'center', padding: '40px', color: '#718096' }}>
                      {translate('No competitions added yet')}
                    </td>
                  </tr>
                ) : (
                  competitions.map((competition, index) => (
                    <tr key={index}>
                      <td>
                        <span className={styles.rankNumber}>{index + 1}</span>
                      </td>
                      <td>
                        <span className={styles.competitionName}>{competition.name}</span>
                      </td>
                      <td>
                        <span className={styles.pointsValue}>{competition.points}</span>
                      </td>
                      {canEdit && (
                        <td>
                          <button
                            className={styles.deleteButton}
                            onClick={() => removeCompetition(index)}
                            title={translate('Delete')}
                          >
                            🗑️
                          </button>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {canEdit && (
            <div className={styles.addButtonContainer}>
              <button
                className={styles.addCompetitionButton}
                onClick={() => setIsModalOpen(true)}
              >
                <span className={styles.buttonIcon}>+</span>
                {translate('Add Competition')}
              </button>
            </div>
          )}
        </div>

        {/* Right Side (Leaderboard with Winners and Classes) */}
        <div className={styles.leaderboard}>
          <h2>{translate('Leaderboard')}</h2>
          <div className={styles.scrollableBody}>
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>{translate('Winner')}</th>
                  <th>{translate('Score')}</th>
                  {canEdit && <th>{translate('Action')}</th>}
                </tr>
              </thead>
              <tbody>
                {displayedWinners.length === 0 && classes.length === 0 ? (
                  <tr>
                    <td colSpan={canEdit ? "4" : "3"} style={{ textAlign: 'center', padding: '40px', color: '#718096' }}>
                      {translate('No winners or classes yet')}
                    </td>
                  </tr>
                ) : (
                  <>
                    {/* Vinnare i leaderboard */}
                    {classes.map((cls, index) => (
                      <tr key={`class-${index}`}>
                        <td>
                          <span className={styles.rankNumber}>{index + 1}</span>
                        </td>
                        <td>
                          <span className={styles.winnerName}>
                            {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : ''} {cls.name}
                          </span>
                        </td>
                        <td>
                          <span className={styles.scoreValue}>{cls.score || 0}</span>
                        </td>
                        {canEdit && (
                          <td>
                            <button
                              className={styles.deleteButton}
                              onClick={() => removeClass(cls.id)}
                              title={translate('Delete')}
                            >
                              🗑️
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Classes Carousel Section */}
      {classes.length > 0 && (
        <div className={styles.classesCarouselSection}>
          <div className={styles.carouselHeader}>
            <h2 className={styles.carouselTitle}>👥 Deltagande Klasser</h2>
            <p className={styles.carouselSubtitle}>Se alla klasser som tävlar</p>
          </div>

          {/* Tydlig linje ovanför */}
          <div className={styles.carouselDividerTop}></div>

          <div className={styles.carouselContainer}>
            <div className={styles.carouselWrapper}>
              <div
                className={styles.carouselTrack}
                style={{
                  transform: `translateX(-${currentSlide * 320}px)`,
                  transition: 'transform 1.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
                }}
              >
                {/* Duplicera klasserna tre gånger för oändlig loop */}
                {[...classes, ...classes, ...classes].map((classItem, index) => {
                  const realIndex = index % classes.length;
                  const rank = realIndex + 1;
                  return (
                    <div key={`${classItem.id || classItem.name}-${index}`} className={styles.carouselSlide}>
                      <div className={styles.classCard}>
                        {/* Dribbble-inspirerad bildsektion */}
                        <div className={styles.classImageSection}>
                          <div className={styles.classIcon}>
                            {rank === 1 ? '🥇' :
                              rank === 2 ? '🥈' :
                                rank === 3 ? '🥉' :
                                  '👥'}
                          </div>
                        </div>

                        <div className={styles.classCardContent}>
                          <h3 className={styles.className}>{classItem.name || 'Klass'}</h3>
                          <div className={styles.classScoreContainer}>
                            <span className={styles.classScoreLabel}>Poäng</span>
                            <span className={styles.classScoreValue}>{classItem.score || 0}</span>
                          </div>
                          <div className={styles.classBadge}>
                            {rank <= 3 ? 'Top 3' : 'Deltagare'}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Tydlig linje under */}
          <div className={styles.carouselDividerBottom}></div>
        </div>
      )}



      {/* Modal för att lägga till tävling - visas endast för lärare */}
      {isModalOpen && canEdit && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <h3>{translate('Add a New Competition')}</h3>
            <input
              type="text"
              placeholder={translate('Competition Name')}
              value={newCompetitionName}
              onChange={(e) => setNewCompetitionName(e.target.value)}
              onKeyPress={handleKeyPress}
            />
            <select
              value={newCompetitionType}
              onChange={(e) => setNewCompetitionType(e.target.value)}
            >
              <option value="time">{translate('Time')}</option>
              <option value="quantity">{translate('Quantity')}</option>
            </select>
            <input
              type="text"
              placeholder={translate('Enter Points')}
              value={newCompetitionPoints}
              onChange={(e) => setNewCompetitionPoints(e.target.value)}
              onKeyPress={handleKeyPress}
            />
            <div className={styles.modalButtons}>
              <button className={styles.uniqueButton} onClick={() => {
                addCompetition();
                setIsModalOpen(false);
              }}>{translate('Add Competition')}</button>
              <button className={styles.uniqueButton} onClick={() => setIsModalOpen(false)}>{translate('Cancel')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default withEditRights(HomePage);
